import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaMobileAlt,
  FaShieldAlt,
  FaDatabase,
  FaUserCog
} from 'react-icons/fa';
import '../css/CutCutBarbershopShowcase.css';

import screenLanding from '../assets/cutcut/landing.png';
import screenTestimonial from '../assets/cutcut/testimonial.png';
import screenDashboard from '../assets/cutcut/dashboard.png';
import screenServices from '../assets/cutcut/services.png';
import screenAdminDashboard from '../assets/cutcut/admin-dashboard.png';
import screenPayments from '../assets/cutcut/payments.png';
import cutcutQrCode from '../assets/cutcut/cutcut-testflight-qr.png';

const TESTFLIGHT_LINK = 'https://testflight.apple.com/join/MV1AuVHc';

const features = [
  {
    icon: <FaMobileAlt />,
    title: 'Premium Booking UX',
    text: 'Fast, clean barber booking flows with polished mobile-first design.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure Access',
    text: 'Protected user and admin flows with structured authentication.'
  },
  {
    icon: <FaDatabase />,
    title: 'Full-Stack System',
    text: 'Connected frontend, backend logic, payments, bookings, and dashboard data.'
  },
  {
    icon: <FaUserCog />,
    title: 'Admin Management',
    text: 'Control services, appointments, payments, notifications, and platform activity.'
  }
];

const screens = [
  {
    image: screenLanding,
    label: 'Landing',
    title: 'Premium customer-facing landing experience'
  },
  {
    image: screenTestimonial,
    label: 'Trust',
    title: 'Testimonial and conversion-focused flow'
  },
  {
    image: screenDashboard,
    label: 'User',
    title: 'Customer dashboard and quick actions'
  },
  {
    image: screenServices,
    label: 'Booking',
    title: 'Service selection and appointment flow'
  },
  {
    image: screenAdminDashboard,
    label: 'Admin',
    title: 'Admin modules and platform control'
  },
  {
    image: screenPayments,
    label: 'Payments',
    title: 'Revenue, payment status, and refund management'
  }
];

export default function CutCutBarbershopShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screens.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screens.length) % screens.length);
  };

  const activeScreen = screens[activeIndex];

  return (
    <section className="cutcut-showcase-section" id="cutcut-barbershop">
      <div className="cutcut-bg-orb orb-left" />
      <div className="cutcut-bg-orb orb-right" />
      <div className="cutcut-grid-overlay" />
      <div className="cutcut-watermark">CUTCUT BARBERSHOP</div>

      <div className="cutcut-showcase-container">
        <motion.div
          className="cutcut-showcase-left"
          initial={{ opacity: 0, x: -45 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <span className="cutcut-tag">Featured Booking Platform</span>
          <h2>CutCut Barbershop App</h2>
          <p>
            A modern barber booking platform built with a premium visual system
            for customers and admin operators. It combines service discovery,
            appointment booking, account flows, payments, and dashboard control
            into one clean mobile product.
          </p>

          <div className="cutcut-feature-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="cutcut-feature-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <div className="cutcut-feature-icon">{feature.icon}</div>
                <div>
                  <h4>{feature.title}</h4>
                  <p>{feature.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="cutcut-stat-row">
            <div className="cutcut-stat-box">
              <strong>2</strong>
              <span>Customer + Admin</span>
            </div>
            <div className="cutcut-stat-box">
              <strong>6</strong>
              <span>Core Screens</span>
            </div>
            <div className="cutcut-stat-box">
              <strong>Booking</strong>
              <span>Payments + Control</span>
            </div>
          </div>

          <div className="cutcut-qr-panel">
            <div className="cutcut-qr-copy">
              <span className="cutcut-qr-tag">iOS Beta Access</span>
              <h4>Scan QR for TestFlight</h4>
              <p>
                Open the CutCut Barbershop beta instantly on your iPhone with
                the TestFlight invite.
              </p>
            </div>

            <a
              href={TESTFLIGHT_LINK}
              target="_blank"
              rel="noreferrer"
              className="cutcut-qr-link"
              aria-label="Open CutCut TestFlight"
            >
              <img
                src={cutcutQrCode}
                alt="CutCut Barbershop TestFlight QR code"
                className="cutcut-qr-image"
              />
            </a>
          </div>

          <div className="cutcut-buttons">
            <a
              href={TESTFLIGHT_LINK}
              target="_blank"
              rel="noreferrer"
              className="cutcut-btn cutcut-btn-primary"
            >
              Join TestFlight
            </a>

            <a
              href="#contact"
              className="cutcut-btn cutcut-btn-secondary"
            >
              Contact Me
            </a>
          </div>
        </motion.div>

        <motion.div
          className="cutcut-showcase-right"
          initial={{ opacity: 0, x: 45 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="cutcut-screen-panel">
            <div className="cutcut-screen-topbar">
              <div className="cutcut-screen-meta">
                <span className="cutcut-screen-label">{activeScreen.label}</span>
                <h3>{activeScreen.title}</h3>
              </div>

              <div className="cutcut-screen-controls">
                <button type="button" onClick={prevSlide} aria-label="Previous screen">
                  <FaChevronLeft />
                </button>
                <button type="button" onClick={nextSlide} aria-label="Next screen">
                  <FaChevronRight />
                </button>
              </div>
            </div>

            <div className="cutcut-phone-stage">
              <motion.div
                className="cutcut-phone-card cutcut-phone-back left"
                animate={{ y: [0, -8, 0], rotate: [-8, -10, -8] }}
                transition={{ repeat: Infinity, duration: 5.6, ease: 'easeInOut' }}
              >
                <img
                  src={screens[(activeIndex + 1) % screens.length].image}
                  alt="CutCut Barbershop preview background left"
                />
              </motion.div>

              <motion.div
                className="cutcut-phone-card cutcut-phone-back right"
                animate={{ y: [0, -10, 0], rotate: [8, 10, 8] }}
                transition={{ repeat: Infinity, duration: 5.9, ease: 'easeInOut' }}
              >
                <img
                  src={screens[(activeIndex + 2) % screens.length].image}
                  alt="CutCut Barbershop preview background right"
                />
              </motion.div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  className="cutcut-phone-card cutcut-phone-main"
                  initial={{ opacity: 0, y: 30, scale: 0.94, rotate: -2 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, y: -18, scale: 0.96, rotate: 2 }}
                  transition={{ duration: 0.45 }}
                >
                  <div className="cutcut-phone-notch" />
                  <img
                    src={activeScreen.image}
                    alt={activeScreen.title}
                    className="cutcut-phone-image"
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="cutcut-screen-thumbs">
              {screens.map((screen, index) => (
                <button
                  key={screen.label}
                  type="button"
                  className={`cutcut-thumb ${activeIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show ${screen.label}`}
                >
                  <img src={screen.image} alt={screen.label} />
                </button>
              ))}
            </div>

            <a
              href={TESTFLIGHT_LINK}
              target="_blank"
              rel="noreferrer"
              className="cutcut-inline-link"
            >
              Open TestFlight <FaArrowRight />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}